import { useEffect, useRef, useState } from 'react';
import { bandsintownRequest, type BandsintownStatus } from '@/api/artistBandsintown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function BandsintownSettings({ artistId }: { artistId: string }) {
  const [status, setStatus] = useState<BandsintownStatus | null>(null);
  const [url, setUrl] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmChange, setConfirmChange] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    setKey(''); setStatus(null); setUrl(''); setError(''); setBusy(false);
    bandsintownRequest(artistId).then(value => {
      if (current === generation.current) { setStatus(value); setUrl(value.profile_url || ''); }
    }).catch(() => { if (current === generation.current) setError('Unable to load Bandsintown settings.'); });
    return () => { generation.current++; };
  }, [artistId]);
  async function act(action: string, method: string, body?: unknown) {
    const current = generation.current;
    setBusy(true); setError('');
    try {
      await bandsintownRequest(artistId, action, method, body);
      if (current !== generation.current) return;
      setKey(''); setConfirmChange(false);
      const latest = await bandsintownRequest(artistId);
      if (current === generation.current) setStatus(latest);
    } catch {
      if (current === generation.current) {
        setError('Action could not complete. Check the connection status and try again.');
        const latest = await bandsintownRequest(artistId).catch(() => null);
        if (current === generation.current && latest) setStatus(latest);
      }
    } finally {
      if (current === generation.current) { setBusy(false); setKey(''); }
    }
  }
  return <section className="aa-glass-card space-y-4 p-5" aria-label="Bandsintown integration">
    <h2 className="text-lg font-semibold">Integrations</h2>
    <h3 className="font-medium">Bandsintown</h3>
    <p className="text-sm text-muted-foreground">This artist workspace shares one connection with its authorized team.</p>
    {status && <>
      <p role="status">Connection: {status.status}. {status.configured ? 'API key saved.' : 'No API key saved.'}</p>
      <p className="text-sm">Last successful sync: {status.last_success_at ? new Date(status.last_success_at).toLocaleString() : 'Never'}</p>
      {!status.live_enabled && <p className="text-sm text-muted-foreground">Live validation and synchronization are awaiting Bandsintown permission approval.</p>}
      <p className="text-sm">Daily synchronization: {status.recurring_enabled ? 'Enabled' : 'Disabled'}</p>
      {status.last_error_code && <p role="status">Attention: {status.last_error_code.replaceAll('_', ' ')}</p>}
      {status.summary && <p className="text-sm">Created {status.summary.created || 0}, updated {status.summary.updated || 0}, review {status.summary.conflicts || 0}, invalid {status.summary.invalid || 0}.</p>}
      {status.can_manage && <div className="space-y-3">
        <label className="block">Bandsintown artist profile URL<Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.bandsintown.com/a/12345" disabled={busy} /></label>
        <label className="block">Bandsintown API key<Input type="password" value={key} onChange={e => setKey(e.target.value)} autoComplete="new-password" disabled={busy} placeholder={status.configured ? 'Leave blank to keep saved key' : 'Enter API key'} /></label>
        <p className="text-xs text-muted-foreground">Get your artist key in Bandsintown for Artists → Settings → General. Saved keys are never displayed.</p>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={confirmChange} onChange={e => setConfirmChange(e.target.checked)} />Confirm replacement if this URL identifies a different artist. Existing shows are retained.</label>
      </div>}
      <div className="flex flex-wrap gap-2">
        {status.can_manage && <>
          <Button type="button" disabled={busy || !url} onClick={() => act('', 'PUT', { profile_url: url, ...(key ? { api_key: key } : {}), revision: status.revision, confirm_identity_change: confirmChange })}>Save connection</Button>
          <Button type="button" disabled={busy || !status.configured || !status.live_enabled} onClick={() => act('/validate', 'POST')}>Validate connection</Button>
          <Button type="button" variant="outline" disabled={busy || !status.configured} onClick={() => act('', 'DELETE')}>Disconnect</Button>
        </>}
        {status.can_sync && <Button type="button" disabled={busy || !status.configured || !status.live_enabled || ['unvalidated', 'disconnected'].includes(status.status)} onClick={() => act('/sync', 'POST')}>Sync Now</Button>}
      </div>
    </>}
    {busy && <p role="status">Working…</p>}
    {error && <p role="alert">{error}</p>}
  </section>;
}
